import React, { useCallback, useEffect, useState } from 'react';
import { getApiUrl } from '../../../utils/apiConfig';

const ServerStatusWidget = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl('/api/system/status'));
      const data = await response.json();
      
      if (data.success) {
        setStatus(data.data);
        setLastUpdated(new Date());
        setError(null);
      } else {
        setError('상태를 불러올 수 없습니다.');
      }
    } catch (err) {
      setError('서버 연결에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    
    // 10분마다 자동 새로고침
    const interval = setInterval(fetchStatus, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const getStatusColor = (overall) => {
    switch (overall) {
      case 'normal': return '#10b981';
      case 'degraded': return '#f59e0b';
      case 'limited': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusText = (overall) => {
    switch (overall) {
      case 'normal': return '정상';
      case 'degraded': return '대체 모델 운영 중';
      case 'limited': return '제한적 운영';
      default: return '확인 중';
    }
  };

  const getStatusIcon = (overall) => {
    switch (overall) {
      case 'normal': return '●';
      case 'degraded': return '◐';
      case 'limited': return '○';
      default: return '◌';
    }
  };

  const formatTime = (date) => {
    if (!date) return '-';
    return date.toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="help-server-status help-server-status--loading">
        <div className="help-server-status__spinner"></div>
        <span>서버 상태 확인 중...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="help-server-status help-server-status--error">
        <span>{error}</span>
        <button onClick={fetchStatus} className="help-server-status__retry">
          다시 시도
        </button>
      </div>
    );
  }

  const overall = status?.service?.overall || 'unknown';
  const message = status?.service?.message || '상태를 확인할 수 없습니다.';
  const providers = status?.providers || {};

  return (
    <div className="help-server-status">
      <div className="help-server-status__header">
        <div className="help-server-status__indicator">
          <span 
            className="help-server-status__dot"
            style={{ color: getStatusColor(overall) }}
          >
            {getStatusIcon(overall)}
          </span>
          <span className="help-server-status__label">
            서비스 상태: <strong style={{ color: getStatusColor(overall) }}>{getStatusText(overall)}</strong>
          </span>
        </div>
        <span className="help-server-status__time">
          마지막 업데이트: {formatTime(lastUpdated)}
        </span>
      </div>
      
      <p className="help-server-status__message">{message}</p>
      
      <div className="help-server-status__providers">
        {Object.entries(providers).map(([name, info]) => (
          <div key={name} className="help-server-status__provider">
            <span className="help-server-status__provider-name">
              {name === 'gemini' ? 'Google Gemini' : 
               name === 'openai' ? 'OpenAI' : 
               name === 'anthropic' ? 'Anthropic Claude' : name}
            </span>
            <span 
              className="help-server-status__provider-status"
              style={{ 
                color: info.is_limited ? '#ef4444' : 
                       info.fallback_active ? '#f59e0b' : '#10b981'
              }}
            >
              {info.is_limited ? 'Rate Limit' : 
               info.fallback_active ? '대체 모델' : '정상'}
            </span>
          </div>
        ))}
      </div>
      
      <button onClick={fetchStatus} className="help-server-status__refresh">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M23 4v6h-6M1 20v-6h6"/>
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
        </svg>
        새로고침
      </button>
    </div>
  );
};

export default ServerStatusWidget;
